import { useCallback } from 'react';

import {
  buildPhaseService,
  type PhaseBuildExecutionState,
} from '../services/buildPhaseService';
import { useGetPhaseBuildStateQuery } from '../store/services/phaseBuildApi';
import { isPhaseResolutionRequired } from '../utils/phaseBuildPresentation';

type RefreshPhaseState = (quiet?: boolean) => Promise<void>;

export {
  createPhaseBuildEventDetail,
  formatPhaseCredits,
  getPhaseBuildBusyLabel,
  getPhaseBuildLabel,
  getPhaseBuildTitle,
  isPhaseBuildActionEnabled,
  isPhaseResolutionRequired,
} from '../utils/phaseBuildPresentation';

export const FOCUS_PHASE_TASKS_EVENT = 'focus_phase_tasks';

const openPhaseTasks = (
  projectId: string,
  phaseId: string,
): void => {
  const detail = { projectId, phaseId };

  window.dispatchEvent(new globalThis.CustomEvent('navigate_to_tab', { detail: 'tasks' }));
  window.setTimeout(() => {
    window.dispatchEvent(new globalThis.CustomEvent('switch_tasks_view', { detail: 'kanban' }));
    window.setTimeout(() => {
      window.dispatchEvent(new globalThis.CustomEvent(FOCUS_PHASE_TASKS_EVENT, { detail }));
    }, 50);
  }, 0);
};

const repairDependencyOrder = async (
  projectId: string,
  state: PhaseBuildExecutionState,
): Promise<void> => {
  if (state.blocking_reason !== 'dependencies') return;

  await buildPhaseService.autoArrangePhases(projectId);
  window.dispatchEvent(new Event('refresh_tasks'));
};

const useResolveCurrentPhase = (
  projectId: string | null | undefined,
  state: PhaseBuildExecutionState | null,
  refresh: RefreshPhaseState,
) => useCallback(async (): Promise<boolean> => {
  const phaseId = state?.current_phase?.id;

  if (!projectId || !phaseId || !state || !isPhaseResolutionRequired(state)) return false;

  await repairDependencyOrder(projectId, state);
  openPhaseTasks(projectId, phaseId);
  await refresh(true);

  return true;
}, [projectId, refresh, state]);

const usePhaseBuildStateResult = (
  projectId: string | null | undefined,
  state: PhaseBuildExecutionState | null,
  isLoading: boolean,
  error: string | null,
  refresh: RefreshPhaseState,
) => {
  const resolveCurrentPhase = useResolveCurrentPhase(projectId, state, refresh);

  return {
    state,
    isLoading,
    error,
    refresh,
    canResolve: isPhaseResolutionRequired(state),
    resolveCurrentPhase,
  };
};

export const usePhaseBuildState = (
  projectId: string | null | undefined,
  modelTier = 'super_large',
) => {
  const query = useGetPhaseBuildStateQuery(
    { projectId: projectId || '', modelTier },
    { skip: !projectId },
  );
  const refetch = query.refetch;
  const refresh = useCallback((_quiet = false): Promise<void> => {
    if (!projectId) return Promise.resolve();

    return refetch()
      .unwrap()
      .then(() => undefined)
      .catch((requestError: unknown) => {
        console.error('Failed to load phase build state:', requestError);
      });
  }, [projectId, refetch]);

  return usePhaseBuildStateResult(
    projectId,
    query.currentData ?? null,
    Boolean(projectId) && query.isFetching && !query.currentData,
    query.isError ? 'Failed to load build phase' : null,
    refresh,
  );
};
